// recova GmbH – Techniker (mobil): Morgen-Pickliste mit Tour-Sperre, Tour, Anfahrt, Zeiterfassung, Teile (Van/Halle/Bestellung), Live-Kosten, Ergebnis, Check-Out, Tour-Ende mit Rücklagerung, 7-Tage-Übersicht.
import { useEffect, useState, useCallback } from "react";
import { API, fmtErr, eur, fileUrl, uploadPhoto, STATUS_LABELS } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { Navigation, CheckCircle2, PackageSearch, ShoppingCart, Ban, CreditCard, QrCode, FileText, ChevronLeft, Camera, CalendarCheck, Mail, Timer, Truck, Warehouse, Square, Trash2, Euro, ClipboardCheck, CalendarDays, PackageOpen, AlertTriangle, ScanBarcode } from "lucide-react";
import { toast } from "sonner";

export default function TechDashboard() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ part_name: "", photo_url: "", note: "" });
  const [costs, setCosts] = useState(null);
  const [quote, setQuote] = useState(null);
  const [payMethod, setPayMethod] = useState(null);
  const [signature, setSignature] = useState(null);
  const [showFollowup, setShowFollowup] = useState(false);
  const [followup, setFollowup] = useState({ date: "", window: "" });
  const [confirmAbort, setConfirmAbort] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [doneJobs, setDoneJobs] = useState(null);
  const [tour, setTour] = useState(null);
  const [tab, setTab] = useState("tour");
  const [calendar, setCalendar] = useState(null);
  const [unload, setUnload] = useState(null);
  const [unloadActions, setUnloadActions] = useState({});
  const [confirmStart, setConfirmStart] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState("picklist");
  const [summary, setSummary] = useState(null);

  const loadJobs = useCallback(
    () => API.get("/tech/jobs").then((r) => setJobs(r.data)).catch((e) => toast.error(fmtErr(e))),
    []
  );
  const loadTour = useCallback(
    () => API.get("/tech/tour/today").then((r) => setTour(r.data)).catch((e) => toast.error(fmtErr(e))),
    []
  );
  useEffect(() => { loadJobs(); loadTour(); }, [loadJobs, loadTour]);

  const loadCosts = useCallback(async (id) => {
    try {
      const r = await API.get(`/tech/jobs/${id}/costs`);
      setCosts(r.data);
    } catch (e) {}
  }, []);

  const working = selected?.work_started_at && !selected?.resolution;

  useEffect(() => {
    if (!working) return;
    loadCosts(selected.id);
    const t = setInterval(() => loadCosts(selected.id), 30000);
    return () => clearInterval(t);
  }, [working, selected?.id, loadCosts]);

  useEffect(() => {
    if (!selected?.work_started_at || selected?.work_ended_at || selected?.resolution) return;
    const start = new Date(selected.work_started_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [selected?.work_started_at, selected?.work_ended_at, selected?.resolution, selected?.id]);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  const openJob = (j) => {
    setSelected(j);
    setQuote(null);
    setPayMethod(null);
    setSignature(null);
    setSearchQ("");
    setSearchResults(null);
    setSupplierOpen(false);
    setConfirmAbort(false);
    setCosts(null);
  };

  const call = async (fn, successMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (successMsg) toast.success(successMsg);
      return r?.data ?? r;
    } catch (e) {
      toast.error(fmtErr(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const reloadSelected = async (id) => {
    const r = await API.get("/tech/jobs");
    setJobs(r.data);
    const j = r.data.find((x) => x.id === id);
    if (j) {
      setSelected(j);
      loadCosts(id);
    }
  };

  const decidePart = async (partId, decision, label) => {
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/parts/decide/${partId}`, { decision }));
    if (d) {
      toast.success(decision === "install" ? `${label} verbaut` : `${label} nicht benötigt – Reservierung aufgehoben`);
      await reloadSelected(selected.id);
    }
  };

  const toggleDone = async () => {
    if (!showDone && doneJobs === null) {
      try {
        const r = await API.get("/tech/jobs/done");
        setDoneJobs(r.data);
      } catch (e) {
        toast.error(fmtErr(e));
      }
    }
    setShowDone(!showDone);
  };

  const enroute = async () => {
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/enroute`), "Unterwegs – Kunde benachrichtigt");
    if (d) { setSelected(d); loadJobs(); }
  };

  const startWork = async () => {
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/start-work`), "Zeiterfassung läuft");
    if (d) { setSelected(d); loadJobs(); }
  };

  const stopWork = async () => {
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/stop-work`), "Zeiterfassung beendet");
    if (d) { setSelected(d); loadCosts(d.id); }
  };

  const searchParts = async (q) => {
    setSearchQ(q);
    if (q.trim().length < 2) return setSearchResults(null);
    setSearching(true);
    try {
      const r = await API.get("/tech/part-search", { params: { q } });
      setSearchResults(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSearching(false);
    }
  };

  const addPart = async (p) => {
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/parts/add`, { part_id: p.id, quantity: 1 }));
    if (d) {
      setSelected(d.order);
      loadCosts(d.order.id);
      toast.success(d.placed === "van" ? `${p.sku} aus dem Van verbaut` : `${p.sku} in der Halle reserviert (Pickliste)`);
    }
  };

  const removePart = async (ref, label) => {
    const d = await call(() => API.delete(`/tech/jobs/${selected.id}/parts/${encodeURIComponent(ref)}`));
    if (d) {
      const jobs = (await API.get("/tech/jobs")).data;
      setJobs(jobs);
      const j = jobs.find((x) => x.id === selected.id);
      if (j) setSelected(j);
      loadCosts(selected.id);
      toast.success(`${label} entfernt`);
    }
  };

  const orderPart = async () => {
    if (!supplierForm.part_name.trim()) return toast.error("Teilebezeichnung erforderlich");
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/parts/order`, {
      part_name: supplierForm.part_name, photo_url: supplierForm.photo_url || null, note: supplierForm.note || null,
    }));
    if (d) {
      setSelected(d.order);
      loadCosts(d.order.id);
      setSupplierOpen(false);
      setSupplierForm({ part_name: "", photo_url: "", note: "" });
      toast.success("Bestellung an Lieferanten ausgelöst – Büro informiert");
    }
  };

  const openCheckout = async (orderId) => {
    const q = (await API.get(`/tech/jobs/${orderId}/quote`)).data;
    setQuote(q);
    setPayMethod(null);
    setSignature(null);
  };

  const finish = async (outcome) => {
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/finish`, { outcome }));
    if (!d) return;
    setSelected(d);
    setConfirmAbort(false);
    loadJobs();
    const scenario = d.resolution?.scenario;
    if (scenario === "c") {
      setShowFollowup(true);
    } else {
      await openCheckout(d.id);
    }
  };

  const doCheckout = async () => {
    if (!payMethod) return toast.error("Zahlungsmethode wählen");
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/checkout`, { method: payMethod, signature }), "Zahlung erfasst – Auftrag aktualisiert");
    if (d) {
      setQuote(null);
      setPayMethod(null);
      setSignature(null);
      setSelected(d);
      loadJobs();
    }
  };

  const doFollowup = async () => {
    if (!followup.date || !followup.window) return toast.error("Datum und Zeitfenster wählen");
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/followup`, followup), "Zweittermin vereinbart – Kunde benachrichtigt");
    if (d) {
      setSelected(d);
      setShowFollowup(false);
      loadJobs();
    }
  };

  const emailInvoice = async () => {
    const d = await call(() => API.post(`/tech/jobs/${selected.id}/invoice/email`));
    if (d) toast.success("Rechnung wird per E-Mail an den Kunden gesendet");
  };

  const decidePick = async (item, state) => {
    const d = await call(() => API.post(`/tech/tour/item/${item.id}`, { state }));
    if (d) {
      toast.success(state === "loaded" ? `${item.sku} eingeladen` : `${item.sku} nicht mitgenommen – Büro wird informiert`);
      loadTour();
    }
  };

  const startTour = async (force) => {
    const d = await call(() => API.post("/tech/tour/start", { force }), "Tour gestartet – gute Fahrt!");
    if (d) { setConfirmStart(false); loadTour(); }
  };

  const openUnload = async () => {
    setBusy(true);
    try {
      const r = await API.get("/tech/tour/unload");
      setUnload(r.data);
      setConfirmEnd(true);
      const actions = {};
      r.data.forEach((p) => { actions[p.part_id] = "restock"; });
      setUnloadActions(actions);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const endTour = async () => {
    const items = (unload || []).map((p) => ({ part_id: p.part_id, action: unloadActions[p.part_id] || "keep" }));
    const d = await call(() => API.post("/tech/tour/end", { items }), "Tour beendet – Rücklagerung verbucht");
    if (d) {
      setConfirmEnd(false);
      setUnload(null);
      loadTour();
      loadJobs();
      try {
        const s = await API.get("/tech/tour/summary");
        setSummary(s.data);
      } catch (e) {}
    }
  };

  const openSummary = async () => {
    try {
      const s = await API.get("/tech/tour/summary");
      setSummary(s.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openScanner = (target) => {
    setScanTarget(target);
    setScanOpen(true);
  };

  const handleScan = (code) => {
    setScanOpen(false);
    const c = code.trim().toLowerCase();
    if (scanTarget === "picklist") {
      const item = (tour?.items || []).find((i) => i.sku.toLowerCase() === c);
      if (!item) return toast.error(`Kein Picklisten-Teil mit Nummer ${code.trim()}`);
      if (item.load_state !== "pending") return toast.info(`${item.sku} wurde bereits entschieden`);
      decidePick(item, "loaded");
    } else {
      searchParts(code.trim());
    }
  };

  const loadCalendar = async () => {
    try {
      const r = await API.get("/tech/calendar");
      setCalendar(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const switchTab = (t) => {
    setTab(t);
    if (t === "kalender" && !calendar) loadCalendar();
  };

  const tourStarted = !!tour?.tour?.started_at;
  const tourEnded = !!tour?.tour?.ended_at;
  const deDate = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

  if (summary) {
    return (
      <DashboardLayout title="Tagesabschluss" subtitle={`Zusammenfassung · ${deDate(summary.date)}`}>
        <div className="mx-auto max-w-md space-y-3" data-testid="day-summary-screen">
          <div className="rounded-2xl bg-slate-900 p-5 text-white shadow-lg">
            <p className="text-lg font-bold">Feierabend – gut gemacht!</p>
            <p className="mt-1 text-xs text-slate-300">Ihre Tagesbilanz auf einen Blick.</p>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="summary-stats">
            <div className="rounded-2xl bg-white p-3 text-center shadow-sm">
              <p className="font-mono2 text-xl font-bold text-[#1b4332]" data-testid="summary-stops">{summary.stops}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Stopps</p>
            </div>
            <div className="rounded-2xl bg-white p-3 text-center shadow-sm">
              <p className="font-mono2 text-xl font-bold text-[#1b4332]" data-testid="summary-revenue">{eur(summary.revenue)}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Umsatz (bezahlt)</p>
            </div>
            <div className="rounded-2xl bg-white p-3 text-center shadow-sm">
              <p className="font-mono2 text-xl font-bold text-[#1b4332]" data-testid="summary-labor">{summary.labor_minutes}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Arbeitsmin.</p>
            </div>
          </div>
          {summary.open_amount > 0 && (
            <p className="rounded-xl bg-amber-50 p-3 text-center text-xs font-semibold text-amber-700" data-testid="summary-open-amount">
              Offene Rechnungen (Überweisung): {eur(summary.open_amount)}
            </p>
          )}
          <div className="rounded-2xl bg-white p-4 shadow-sm" data-testid="summary-jobs">
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Aufträge heute</p>
            {summary.jobs.length === 0 && <p className="text-sm text-slate-400">Keine bearbeiteten Aufträge.</p>}
            {summary.jobs.map((j) => (
              <div key={j.order_number} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs last:border-0" data-testid={`summary-job-${j.order_number}`}>
                <span><span className="font-mono2 font-bold">{j.order_number}</span> · {j.customer}</span>
                <span className={j.paid ? "font-bold text-[#1b4332]" : "font-semibold text-orange-600"}>
                  {j.amount != null ? `${eur(j.amount)} ${j.paid ? "✓" : "offen"}` : "–"}
                </span>
              </div>
            ))}
          </div>
          {summary.restocked.length > 0 && (
            <div className="rounded-2xl bg-white p-4 shadow-sm" data-testid="summary-restocked">
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Zurück eingelagert</p>
              {summary.restocked.map((r) => (
                <p key={r.part_id} className="py-0.5 text-xs text-slate-600" data-testid={`summary-restocked-${r.sku}`}>
                  <span className="font-mono2 font-semibold">{r.sku}</span> {r.name} ({r.quantity}x) → {r.bin}
                </p>
              ))}
            </div>
          )}
          <button className="w-full rounded-2xl bg-[#1b4332] py-3.5 text-sm font-bold text-white shadow-lg transition-all active:scale-95"
            onClick={() => setSummary(null)} data-testid="summary-close">
            Zur Tagesübersicht
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (selected) {
    const hasPendingParts = (selected.reserved_parts?.length || 0) + (selected.ordered_parts?.length || 0) > 0;
    return (
      <DashboardLayout title={`Auftrag ${selected.order_number}`} subtitle={`Techniker · ${STATUS_LABELS[selected.status] || selected.status}`}>
        <button className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#028090] hover:underline" onClick={() => setSelected(null)} data-testid="back-to-jobs">
          <ChevronLeft className="h-4 w-4" /> Zurück zur Tour
        </button>
        <div className="mx-auto max-w-md overflow-hidden rounded-3xl bg-slate-900 p-4 text-white shadow-2xl sm:p-6" data-testid="tech-job-detail">
          <div className="rounded-2xl bg-white/10 p-4">
            <p className="text-lg font-bold">{selected.customer?.name}</p>
            <p className="text-sm text-slate-300">{selected.customer?.street}, {selected.customer?.zip} {selected.customer?.city}</p>
            <div className="mt-1 space-y-0.5" data-testid="customer-contact">
              <a href={`tel:${selected.customer?.phone}`} className="block text-sm font-semibold text-[#2ec4b6]" data-testid="customer-phone-link">{selected.customer?.phone}</a>
              {selected.customer?.email && (
                <a href={`mailto:${selected.customer.email}`} className="block text-xs text-slate-300" data-testid="customer-email-link">{selected.customer.email}</a>
              )}
            </div>
            <a className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#028090] px-3 py-1.5 text-xs font-semibold text-white"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${selected.customer?.street}, ${selected.customer?.zip} ${selected.customer?.city}`)}`}
              target="_blank" rel="noreferrer" data-testid="navigate-button">
              <Navigation className="h-3.5 w-3.5" /> Navigation starten
            </a>
          </div>
          <div className="mt-3 rounded-2xl bg-white/10 p-4">
            <p className="text-sm font-semibold">{selected.appliance?.brand} {selected.appliance?.type_label} {selected.appliance?.model}</p>
            {selected.appliance?.e_number && <p className="font-mono2 text-xs text-slate-300">E-Nr.: {selected.appliance.e_number}</p>}
            {selected.appliance?.serial_number && <p className="font-mono2 text-xs text-slate-300">Seriennr.: {selected.appliance.serial_number}</p>}
            <p className="mt-1 text-sm text-slate-300">{[selected.fault?.error_code, ...(selected.fault?.issues || []), selected.fault?.description].filter(Boolean).join(" · ")}</p>
            <div className="mt-2 flex gap-2">
              {selected.appliance?.type_photo && <img src={fileUrl(selected.appliance.type_photo)} alt="Typenschild" className="h-16 rounded-lg object-cover" data-testid="job-type-photo" />}
              {selected.fault?.photo && <img src={fileUrl(selected.fault.photo)} alt="Schaden" className="h-16 rounded-lg object-cover" data-testid="job-fault-photo" />}
            </div>
          </div>

          {selected.reserved_parts?.length > 0 && !working && !selected.resolution && (
            <div className="mt-3 rounded-2xl border border-[#2ec4b6]/40 bg-[#1b4332]/40 p-4" data-testid="dispo-parts-card">
              <p className="text-xs font-bold uppercase tracking-widest text-[#2ec4b6]">Von der Disposition verplante Teile</p>
              {selected.reserved_parts.map((p) => (
                <p key={p.part_id} className="mt-1 text-xs text-slate-200" data-testid={`dispo-part-${p.sku}`}>
                  {p.sku} – {p.name} ({p.quantity}x) · Lagerplatz: {p.bin}
                </p>
              ))}
              <p className="mt-1.5 text-[10px] text-slate-400">Nach Arbeitsbeginn entscheiden Sie, ob das Teil benötigt wird.</p>
            </div>
          )}

          {["termin_vereinbart", "bereit_fuer_zweittermin", "teil_reserviert_halle"].includes(selected.status) && (
            <button className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2ec4b6] py-3.5 font-bold text-[#1b4332] transition-all active:scale-95" onClick={enroute} disabled={busy} data-testid="enroute-button">
              <Navigation className="h-5 w-5" /> Anfahrt starten – „Techniker unterwegs"
            </button>
          )}

          {selected.status === "techniker_unterwegs" && !selected.work_started_at && (
            <button className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-white py-3.5 font-bold text-[#1b4332] transition-all active:scale-95" onClick={startWork} disabled={busy} data-testid="start-work-button">
              <Play2 /> Angekommen – Zeiterfassung starten
            </button>
          )}

          {working && (
            <div className="mt-4 space-y-3" data-testid="work-panel">
              <div className="rounded-2xl bg-[#1b4332] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2" data-testid="work-timer">
                    <Timer className={`h-5 w-5 text-[#2ec4b6] ${selected.work_ended_at ? "" : "animate-pulse"}`} />
                    {selected.work_ended_at ? (
                      <span className="font-mono2 text-lg font-bold text-white" data-testid="work-ended-label">{selected.labor_minutes} Min.</span>
                    ) : (
                      <span className="font-mono2 text-2xl font-bold text-white">{mm}:{ss}</span>
                    )}
                    <span className="text-xs text-emerald-100/70">{selected.work_ended_at ? "Zeit erfasst" : "läuft"}</span>
                  </div>
                  {!selected.work_ended_at && (
                    <button className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-[#1b4332] transition-all active:scale-95" onClick={stopWork} disabled={busy} data-testid="stop-work-button">
                      <Square className="mr-1 inline h-3.5 w-3.5" /> Zeiterfassung beenden
                    </button>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-white/10 p-4" data-testid="parts-panel">
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Ersatzteile</p>
                {(selected.parts_used || []).map((p) => (
                  <div key={`used-${p.part_id}`} className="mb-1.5 flex items-center justify-between rounded-lg bg-[#028090]/25 px-2.5 py-2 text-xs" data-testid={`installed-part-${p.sku}`}>
                    <span><Truck className="mr-1 inline h-3 w-3" />{p.sku} – {p.name} ({p.quantity}x) · {eur(p.vk * p.quantity)}</span>
                    <button onClick={() => removePart(p.part_id, p.sku)} className="text-red-300 hover:text-red-200" data-testid={`remove-part-${p.sku}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {(selected.reserved_parts || []).map((p) => (
                  <div key={`res-${p.part_id}`} className="mb-1.5 rounded-lg bg-[#1b4332]/60 px-2.5 py-2 text-xs" data-testid={`reserved-part-${p.sku}`}>
                    <span><Warehouse className="mr-1 inline h-3 w-3" />{p.sku} – {p.name} ({p.quantity}x) · reserviert: {p.bin}</span>
                    <span className="mt-1.5 flex gap-2">
                      <button className="flex-1 rounded-lg bg-[#2ec4b6] px-2 py-1.5 font-bold text-[#1b4332] transition-all active:scale-95"
                        onClick={() => decidePart(p.part_id, "install", p.sku)} disabled={busy} data-testid={`install-part-${p.sku}`}>
                        Wird benötigt – verbauen
                      </button>
                      <button className="flex-1 rounded-lg bg-slate-600 px-2 py-1.5 font-bold text-white transition-all active:scale-95"
                        onClick={() => decidePart(p.part_id, "decline", p.sku)} disabled={busy} data-testid={`decline-part-${p.sku}`}>
                        Nicht benötigt
                      </button>
                    </span>
                  </div>
                ))}
                {(selected.ordered_parts || []).map((p) => (
                  <div key={`ord-${p.part_name}`} className="mb-1.5 flex items-center justify-between rounded-lg bg-amber-600/30 px-2.5 py-2 text-xs" data-testid={`ordered-part-${p.part_name.slice(0, 12).replace(/\W+/g, "-")}`}>
                    <span><ShoppingCart className="mr-1 inline h-3 w-3" />{p.part_name} ({p.quantity}x) · bestellt</span>
                    <button onClick={() => removePart(p.part_name, p.part_name)} className="text-red-300 hover:text-red-200" data-testid="remove-ordered-part"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <div className="mt-2 flex gap-2">
                  <Input placeholder="Ersatzteil suchen (Name oder Nummer)…" value={searchQ} onChange={(e) => searchParts(e.target.value)}
                    className="flex-1 bg-white text-slate-900" data-testid="tech-part-search-input" />
                  <button className="rounded-xl bg-[#028090] px-3.5 text-white transition-all active:scale-95" onClick={() => openScanner("partsearch")}
                    data-testid="part-search-scan-button" title="Barcode scannen">
                    <ScanBarcode className="h-4 w-4" />
                  </button>
                </div>
                {searching && <p className="mt-1.5 text-xs text-slate-300">Suche läuft…</p>}
                {searchResults && (
                  <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto" data-testid="tech-part-results">
                    {searchResults.map((p) => (
                      <div key={p.id} className="rounded-lg bg-white p-2.5 text-xs text-slate-800" data-testid={`tech-part-result-${p.sku}`}>
                        <span className="font-mono2 font-bold">{p.sku}</span> <span className="font-semibold">{p.name}</span> · {eur(p.vk)}
                        <span className="mt-1 flex flex-wrap items-center gap-1.5">
                          {p.van_qty > 0
                            ? <span className="rounded-full bg-[#028090] px-2 py-0.5 font-bold text-white"><Truck className="mr-1 inline h-3 w-3" />Van: {p.van_qty}x</span>
                            : <span className="rounded-full bg-slate-200 px-2 py-0.5 text-slate-500">Van: 0</span>}
                          {p.halle_total > 0
                            ? <span className="rounded-full bg-[#1b4332] px-2 py-0.5 font-bold text-white"><Warehouse className="mr-1 inline h-3 w-3" />Halle: {p.halle_total}x · {p.bins[0]?.bin}</span>
                            : <span className="rounded-full bg-slate-200 px-2 py-0.5 text-slate-500">Halle: 0</span>}
                          {(p.van_qty > 0 || p.halle_total > 0) && (
                            <button className="ml-auto rounded-lg bg-[#2ec4b6] px-2.5 py-1 font-bold text-[#1b4332] transition-all active:scale-95"
                              onClick={() => addPart(p)} disabled={busy} data-testid={`part-add-${p.sku}`}>
                              {p.van_qty > 0 ? "Verbauen" : "Reservieren"}
                            </button>
                          )}
                        </span>
                      </div>
                    ))}
                    {searchResults.length === 0 && <p className="text-xs text-slate-300">Kein Teil gefunden.</p>}
                  </div>
                )}
                {!supplierOpen ? (
                  <button className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-600/80 px-4 py-2.5 text-xs font-bold text-white transition-all active:scale-95"
                    onClick={() => setSupplierOpen(true)} data-testid="supplier-order-toggle">
                    <ShoppingCart className="h-4 w-4" /> Teil nicht verfügbar – beim Lieferanten bestellen
                  </button>
                ) : (
                  <div className="mt-2 space-y-2 rounded-xl bg-white/10 p-3" data-testid="supplier-form">
                    <Input placeholder="Teilebezeichnung *" value={supplierForm.part_name}
                      onChange={(e) => setSupplierForm({ ...supplierForm, part_name: e.target.value })}
                      className="bg-white text-slate-900" data-testid="supplier-part-name" />
                    <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-white/30 p-2.5 text-xs text-slate-300" data-testid="supplier-photo">
                      <Camera className="h-4 w-4" /> {supplierForm.photo_url ? "Foto hochgeladen ✓" : "Foto vom defekten Teil (optional)"}
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => e.target.files[0] && setSupplierForm({ ...supplierForm, photo_url: await uploadPhoto(e.target.files[0]) })} />
                    </label>
                    <Textarea rows={2} placeholder="Notiz (optional)" value={supplierForm.note}
                      onChange={(e) => setSupplierForm({ ...supplierForm, note: e.target.value })}
                      className="bg-white text-slate-900" data-testid="supplier-note" />
                    <div className="flex gap-2">
                      <button className="flex-1 rounded-xl bg-amber-600 py-2.5 text-xs font-bold text-white transition-all active:scale-95" onClick={orderPart} disabled={busy} data-testid="supplier-submit">
                        Bestellung auslösen
                      </button>
                      <button className="rounded-xl bg-slate-600 px-3 py-2.5 text-xs font-bold text-white" onClick={() => setSupplierOpen(false)} data-testid="supplier-cancel">Zurück</button>
                    </div>
                  </div>
                )}
              </div>

              {costs && (
                <div className="rounded-2xl bg-white/10 p-4" data-testid="costs-card">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-400"><Euro className="h-3.5 w-3.5" /> Kostenübersicht (für den Kunden)</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between text-slate-300"><span>Anfahrts- &amp; Diagnosepauschale</span><span>{eur(costs.diagnosis)}</span></div>
                    <div className="flex justify-between text-[#2ec4b6]"><span>Verrechnung bei Reparatur (mind. Pauschale fällig)</span><span>− {eur(costs.credit_amount ?? costs.diagnosis)}</span></div>
                    {costs.labor > 0 && (
                      <div className="flex justify-between text-slate-200" data-testid="costs-labor">
                        <span>Arbeitszeit ({costs.labor_minutes} Min. à {eur(costs.labor_rate)}/h){costs.running ? " · läuft" : ""}</span>
                        <span>{eur(costs.labor)}</span>
                      </div>
                    )}
                    {costs.parts.map((p) => (
                      <div key={p.part_id} className="flex justify-between text-slate-200"><span>{p.name} ({p.quantity}x)</span><span>{eur(p.vk * p.quantity)}</span></div>
                    ))}
                    {(costs.reserved_parts || []).map((p) => (
                      <div key={p.part_id} className="flex justify-between text-slate-400"><span>{p.name} – Zweittermin, noch nicht fällig</span><span>{eur(p.vk * p.quantity)}</span></div>
                    ))}
                    <div className="flex justify-between border-t border-white/20 pt-1.5 text-sm font-bold text-white">
                      <span>Bei Reparatur heute</span><span data-testid="costs-total">{eur(costs.repair_total)}</span>
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-400"><span>davon MwSt. 19 %</span><span>{eur(costs.repair_vat)}</span></div>
                    <div className="flex justify-between text-[11px] text-amber-300"><span>Bei Abbruch fällig (nur Pauschale)</span><span>{eur(costs.abort_total)}</span></div>
                  </div>
                </div>
              )}

              {!selected.work_ended_at ? (
                confirmAbort ? (
                  <div className="rounded-2xl border-2 border-[#e76f51] bg-[#e76f51]/15 p-4" data-testid="abort-confirm-box">
                    <p className="text-sm font-semibold text-white">Auftrag wirklich abbrechen?</p>
                    <p className="mt-1 text-xs text-slate-300">Der Kunde lehnt die Kosten ab – es wird nur die Diagnosepauschale{costs ? ` (${eur(costs.abort_total)})` : ""} berechnet.</p>
                    <div className="mt-3 flex gap-2">
                      <button className="flex-1 rounded-xl bg-[#e76f51] py-2.5 text-sm font-bold text-white transition-all active:scale-95" onClick={() => finish("abgebrochen")} disabled={busy} data-testid="abort-confirm">
                        Ja, abbrechen
                      </button>
                      <button className="flex-1 rounded-xl bg-slate-600 py-2.5 text-sm font-bold text-white" onClick={() => setConfirmAbort(false)} data-testid="abort-cancel">Zurück</button>
                    </div>
                  </div>
                ) : (
                  <button className="w-full rounded-xl border-2 border-[#e76f51] py-3 text-sm font-bold text-[#e76f51] transition-all active:scale-95"
                    onClick={() => setConfirmAbort(true)} data-testid="abort-open">
                    <Ban className="mr-2 inline h-4 w-4" /> Kosten zu hoch – Auftrag abbrechen
                  </button>
                )
              ) : (
                <div className="grid grid-cols-1 gap-2" data-testid="finish-buttons">
                  {!hasPendingParts && (
                    <button className="flex items-center gap-3 rounded-xl bg-[#2a9d8f] px-4 py-3.5 text-left text-sm font-bold text-white transition-all active:scale-95"
                      onClick={() => finish("ok")} disabled={busy} data-testid="finish-ok">
                      <CheckCircle2 className="h-6 w-6" /> OK – Reparatur erfolgreich<span className="ml-auto text-xs font-normal opacity-80">→ Check-Out</span>
                    </button>
                  )}
                  {hasPendingParts && (
                    <button className="flex items-center gap-3 rounded-xl bg-[#028090] px-4 py-3.5 text-left text-sm font-bold text-white transition-all active:scale-95"
                      onClick={() => finish("zweittermin")} disabled={busy} data-testid="finish-followup">
                      <CalendarCheck className="h-6 w-6" /> Zweittermin nötig<span className="ml-auto text-xs font-normal opacity-80">Teil reserviert/bestellt</span>
                    </button>
                  )}
                  <button className="flex items-center gap-3 rounded-xl bg-[#e76f51] px-4 py-3.5 text-left text-sm font-bold text-white transition-all active:scale-95"
                    onClick={() => finish("irreparabel")} disabled={busy} data-testid="finish-irreparabel">
                    <Ban className="h-6 w-6" /> Irreparabel – Totalschaden<span className="ml-auto text-xs font-normal opacity-80">mit Gutschein</span>
                  </button>
                  <button className="flex items-center gap-3 rounded-xl bg-slate-600 px-4 py-3.5 text-left text-sm font-bold text-white transition-all active:scale-95"
                    onClick={() => finish("abgebrochen")} disabled={busy} data-testid="finish-abort">
                    <Ban className="h-6 w-6" /> Abbruch – Kunde lehnt ab<span className="ml-auto text-xs font-normal opacity-80">nur Pauschale</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {selected.resolution && selected.resolution.scenario === "c" && (
            selected.status === "termin_vereinbart" ? (
              <div className="mt-4 rounded-xl bg-white/10 p-3 text-center text-sm" data-testid="followup-info">
                Zweittermin vereinbart: <strong>{selected.scheduled_date} · {selected.time_window}</strong>
                <span className="block text-xs text-slate-300">Das Teil liegt auf der Pickliste und wird ins Fahrzeug geladen.</span>
              </div>
            ) : (
              <button className="mt-4 w-full rounded-xl bg-[#2ec4b6] py-3.5 font-bold text-[#1b4332] transition-all active:scale-95" onClick={() => setShowFollowup(true)} data-testid="followup-open-button">
                <CalendarCheck className="mr-2 inline h-5 w-5" /> Zweittermin mit Kunden vereinbaren
              </button>
            )
          )}

          {selected.resolution && selected.resolution.scenario !== "c" && !selected.payment && (
            <button className="mt-4 w-full rounded-xl bg-white py-3.5 font-bold text-[#1b4332] transition-all active:scale-95" onClick={() => openCheckout(selected.id)} data-testid="checkout-open-button">
              <CreditCard className="mr-2 inline h-5 w-5" /> Check-Out & Zahlung
            </button>
          )}

          {selected.invoice_url && (
            <div className="mt-4 rounded-2xl bg-white/10 p-4" data-testid="tech-invoice-panel">
              <p className="text-xs uppercase tracking-widest text-slate-400">Rechnung & Arbeitsbericht</p>
              {selected.invoice_number && (
                <p className="mt-0.5 font-mono2 text-xs font-bold text-[#2ec4b6]" data-testid="tech-invoice-number">{selected.invoice_number}</p>
              )}
              <p className="mt-1 text-sm">
                Betrag: <strong>{eur(selected.payment?.amount)}</strong>
                {selected.payment && ` · ${selected.payment.method_label} · ${selected.payment.paid ? "Bezahlt" : "Offen"}`}
              </p>
              <div className="mt-2 flex gap-2">
                <a href={fileUrl(selected.invoice_url)} target="_blank" rel="noreferrer"
                  className="flex-1 rounded-lg bg-white px-3 py-2 text-center text-xs font-bold text-[#1b4332] transition-all active:scale-95" data-testid="tech-invoice-pdf">
                  <FileText className="mr-1 inline h-3.5 w-3.5" /> PDF anzeigen
                </a>
                <button className="flex-1 rounded-lg bg-[#028090] px-3 py-2 text-xs font-bold text-white transition-all active:scale-95" onClick={emailInvoice} disabled={busy} data-testid="tech-invoice-email">
                  <Mail className="mr-1 inline h-3.5 w-3.5" /> Per Mail an Kunden
                </button>
              </div>
            </div>
          )}
        </div>

        <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={handleScan}
          title={scanTarget === "picklist" ? "Teil scannen – wird eingeladen" : "Teilenummer scannen"} />
        {showFollowup && (
          <Dialog open onOpenChange={setShowFollowup}>
            <DialogContent className="max-w-md" data-testid="followup-dialog">
              <DialogHeader><DialogTitle>Zweittermin mit Kunden vereinbaren</DialogTitle><DialogDescription className="sr-only">Datum und Zeitfenster für den Zweittermin wählen</DialogDescription></DialogHeader>
              <p className="rounded-lg bg-[#e8f0ec] p-3 text-xs text-[#1b4332]">
                Das reservierte Teil liegt morgen früh auf der Pickliste und wird vor der Tour ins Fahrzeug geladen.
              </p>
              <div className="space-y-3">
                <div>
                  <Label>Datum</Label>
                  <Input type="date" className="mt-1" value={followup.date} onChange={(e) => setFollowup({ ...followup, date: e.target.value })} data-testid="followup-date" />
                </div>
                <div>
                  <Label>Zeitfenster</Label>
                  <select className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" value={followup.window}
                    onChange={(e) => setFollowup({ ...followup, window: e.target.value })} data-testid="followup-window">
                    <option value="">Bitte wählen…</option>
                    {["vormittags (8-12 Uhr)", "nachmittags (12-17 Uhr)", "abends (17-20 Uhr)"].map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
                <Button className="w-full bg-[#1b4332] hover:bg-[#143326]" onClick={doFollowup} disabled={busy} data-testid="followup-submit">
                  {busy ? "Speichern…" : "Termin speichern & Kunde benachrichtigen"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {quote && (
          <Dialog open onOpenChange={() => setQuote(null)}>
            <DialogContent className="max-w-md" data-testid="checkout-dialog">
              <DialogHeader><DialogTitle>Check-Out &amp; Zahlung – {selected.order_number}</DialogTitle><DialogDescription className="sr-only">Zahlungsmethode wählen und Kundenunterschrift erfassen</DialogDescription></DialogHeader>
              <div className="space-y-2 text-sm" data-testid="quote-summary">
                <div className="flex justify-between"><span>Anfahrts- &amp; Diagnosepauschale</span><span className="font-semibold">{eur(quote.diagnosis)}</span></div>
                {quote.diagnosis_credited && <div className="flex justify-between text-[#2a9d8f]"><span>Verrechnung Pauschale (Reparatur erfolgt)</span><span>− {eur(quote.credit_amount ?? quote.diagnosis)}</span></div>}
                {quote.labor > 0 && <div className="flex justify-between"><span>Arbeitszeit ({quote.labor_minutes} Min. à {eur(quote.labor_rate)}/h)</span><span className="font-semibold">{eur(quote.labor)}</span></div>}
                {quote.parts.map((p, i) => <div key={i} className="flex justify-between"><span>{p.name} ({p.quantity}x)</span><span className="font-semibold">{eur(p.vk * p.quantity)}</span></div>)}
                {quote.diagnosis_already_paid > 0 && <div className="flex justify-between text-[#2a9d8f]"><span>Abzug: Bereits gezahlt</span><span>− {eur(quote.diagnosis_already_paid)}</span></div>}
                <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold text-[#1b4332]"><span>Jetzt fällig</span><span data-testid="quote-total">{eur(quote.due_now)}</span></div>
                <div className="flex justify-between text-[11px] text-slate-400" data-testid="quote-vat"><span>davon MwSt. 19 %</span><span>{eur(quote.vat)}</span></div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="payment-methods">
                {[
                  { key: "ec_tap", label: "EC / Kreditkarte", sub: "Tap-to-Pay", icon: <CreditCard className="h-5 w-5" /> },
                  { key: "paypal_qr", label: "PayPal", sub: "QR-Code", icon: <QrCode className="h-5 w-5" /> },
                  { key: "invoice", label: "Rechnung", sub: "Überweisung", icon: <FileText className="h-5 w-5" /> },
                ].map((m) => (
                  <button key={m.key} onClick={() => setPayMethod(m.key)} data-testid={`pay-${m.key}`}
                    className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 text-xs font-semibold transition-all ${payMethod === m.key ? "border-[#1b4332] bg-[#e8f0ec] text-[#1b4332]" : "border-slate-200 text-slate-500 hover:border-[#2ec4b6]"}`}>
                    {m.icon}{m.label}<span className="text-[10px] font-normal">{m.sub}</span>
                  </button>
                ))}
              </div>
              {payMethod === "paypal_qr" && (
                <div className="mt-3 flex flex-col items-center rounded-xl border border-slate-200 p-4" data-testid="paypal-qr-box">
                  <div className="grid h-32 w-32 grid-cols-8 gap-0.5 rounded-lg bg-slate-900 p-2">
                    {Array.from({ length: 64 }).map((_, i) => <div key={i} className={`rounded-[2px] ${(i * 7 + 3) % 3 ? "bg-slate-900" : "bg-white"}`} />)}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Kunde scannt den QR-Code mit der PayPal-App</p>
                </div>
              )}
              {payMethod === "ec_tap" && <p className="mt-3 rounded-lg bg-[#e8f0ec] p-3 text-center text-sm text-[#1b4332]" data-testid="ec-tap-hint">Karte oder Smartphone ans Terminal halten…</p>}
              <div className="mt-4">
                <Label>Kundenunterschrift (Abnahme der Arbeit – erscheint auf der Rechnung)</Label>
                <div className="mt-1.5"><SignaturePad onChange={setSignature} /></div>
              </div>
              <Button className="mt-4 w-full bg-[#1b4332] py-6 text-base hover:bg-[#143326]" onClick={doCheckout} disabled={busy || !payMethod} data-testid="checkout-submit">
                {busy ? "Verarbeite…" : `Zahlung erfassen (${eur(quote.due_now)})`}
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </DashboardLayout>
    );
  }

  if (tour && !tourStarted && !selected) {
    const items = tour.items || [];
    const decided = items.filter((i) => i.load_state !== "pending").length;
    return (
      <DashboardLayout title="Morgen-Pickliste" subtitle={`Fahrzeug beladen · ${deDate(new Date().toISOString().slice(0, 10))}`}>
        <div className="mx-auto max-w-md space-y-3" data-testid="picklist-screen">
          <div className="rounded-2xl bg-slate-900 p-5 text-white shadow-lg">
            <p className="text-sm font-bold" data-testid="picklist-progress">{decided} von {items.length} Positionen erledigt</p>
            <p className="mt-1 text-xs text-slate-300">Bitte jedes Teil am Lagerplatz entnehmen und hier bestätigen, bevor die Tour startet.</p>
          </div>
          {items.length > 0 && (
            <button className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-[#028090] bg-white py-3 text-sm font-bold text-[#028090] transition-all active:scale-95"
              onClick={() => openScanner("picklist")} data-testid="picklist-scan-button">
              <ScanBarcode className="h-5 w-5" /> Teil scannen & einladen
            </button>
          )}
          {items.map((it) => (
            <div key={it.id} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`picklist-item-${it.sku}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono2 text-sm font-bold text-[#0f172a]">{it.sku}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${it.load_state === "loaded" ? "bg-[#1b4332] text-white" : it.load_state === "not_loaded" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`} data-testid={`picklist-state-${it.sku}`}>
                  {it.load_state === "loaded" ? "Eingeladen" : it.load_state === "not_loaded" ? "Nicht mitgenommen" : "Offen"}
                </span>
              </div>
              <p className="mt-1 text-sm font-semibold text-slate-800">{it.part_name} ({it.quantity}x)</p>
              <p className="mt-0.5 text-xs text-slate-500">
                Lagerplatz: <span className="font-mono2 font-semibold text-[#1b4332]">{it.bin}</span>
                {" · "}Auftrag {it.order_number}{it.customer_name ? ` · ${it.customer_name}` : ""}
              </p>
              <div className="mt-2.5 flex gap-2">
                <button className="flex-1 rounded-xl bg-[#2ec4b6] py-2.5 text-xs font-bold text-[#1b4332] transition-all active:scale-95 disabled:opacity-40"
                  onClick={() => decidePick(it, "loaded")} disabled={busy || it.load_state === "loaded"} data-testid={`picklist-load-${it.sku}`}>
                  <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> Eingeladen
                </button>
                <button className="flex-1 rounded-xl bg-slate-600 py-2.5 text-xs font-bold text-white transition-all active:scale-95 disabled:opacity-40"
                  onClick={() => decidePick(it, "not_loaded")} disabled={busy || it.load_state === "not_loaded"} data-testid={`picklist-skip-${it.sku}`}>
                  Nicht mitgenommen
                </button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400" data-testid="picklist-empty">
              Keine reservierten Teile für heute – Sie können direkt starten.
            </div>
          )}
          <button className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1b4332] py-4 text-base font-bold text-white shadow-lg transition-all active:scale-95"
            onClick={() => (tour.undecided > 0 ? setConfirmStart(true) : startTour(false))} disabled={busy} data-testid="tour-start-button">
            <Truck className="h-5 w-5" /> Tour starten{tour.undecided > 0 ? ` (${tour.undecided} offen)` : ""}
          </button>
        </div>

        <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={handleScan}
          title={scanTarget === "picklist" ? "Teil scannen – wird eingeladen" : "Teilenummer scannen"} />
        {confirmStart && (
          <Dialog open onOpenChange={setConfirmStart}>
            <DialogContent className="max-w-md" data-testid="tour-start-confirm-dialog">              <DialogHeader><DialogTitle>Pickliste nicht vollständig</DialogTitle><DialogDescription className="sr-only">Warnung vor Tourstart mit offenen Positionen</DialogDescription></DialogHeader>
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{tour.undecided} Position(en) ohne Entscheidung. Fehlende Teile können unterwegs nicht verbaut werden. Trotzdem starten?</p>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 bg-[#e76f51] hover:bg-[#d45a3a]" onClick={() => startTour(true)} disabled={busy} data-testid="tour-start-force">
                  Trotzdem starten
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setConfirmStart(false)} data-testid="tour-start-cancel">Zurück</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DashboardLayout>
    );
  }

  if (confirmEnd && unload !== null && !selected) {
    return (
      <DashboardLayout title="Tour Ende – Rücklagerung" subtitle="Nicht verwendete Teile wieder einlagern">
        <div className="mx-auto max-w-md space-y-3" data-testid="unload-screen">
          <div className="rounded-2xl bg-slate-900 p-5 text-white shadow-lg">
            <p className="text-sm font-bold">Fahrzeugbestand prüfen</p>
            <p className="mt-1 text-xs text-slate-300">Entscheiden Sie pro Teil: zurück auf den Lagerplatz oder im Fahrzeug belassen.</p>
          </div>
          {unload.map((p) => (
            <div key={p.part_id} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`unload-item-${p.sku}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono2 text-sm font-bold text-[#0f172a]">{p.sku}</span>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-600">{p.quantity}x im Van</span>
              </div>
              <p className="mt-1 text-sm font-semibold text-slate-800">{p.name}</p>
              <div className="mt-2.5 grid grid-cols-2 gap-2">
                <button className={`rounded-xl py-2.5 text-xs font-bold transition-all active:scale-95 ${unloadActions[p.part_id] === "restock" ? "bg-[#1b4332] text-white" : "bg-slate-100 text-slate-500"}`}
                  onClick={() => setUnloadActions({ ...unloadActions, [p.part_id]: "restock" })} data-testid={`unload-restock-${p.sku}`}>
                  <Warehouse className="mr-1 inline h-3.5 w-3.5" /> Einlagern → {p.suggested_bin}
                </button>
                <button className={`rounded-xl py-2.5 text-xs font-bold transition-all active:scale-95 ${unloadActions[p.part_id] === "keep" ? "bg-[#028090] text-white" : "bg-slate-100 text-slate-500"}`}
                  onClick={() => setUnloadActions({ ...unloadActions, [p.part_id]: "keep" })} data-testid={`unload-keep-${p.sku}`}>
                  <Truck className="mr-1 inline h-3.5 w-3.5" /> Im Fahrzeug lassen
                </button>
              </div>
            </div>
          ))}
          {unload.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400" data-testid="unload-empty">
              Fahrzeug ist leer – nichts zurückzulagern.
            </div>
          )}
          <button className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1b4332] py-4 text-base font-bold text-white shadow-lg transition-all active:scale-95"
            onClick={endTour} disabled={busy} data-testid="tour-end-submit">
            <PackageOpen className="h-5 w-5" /> Tour abschließen
          </button>
          <button className="w-full rounded-xl bg-white py-2.5 text-sm font-semibold text-slate-600" onClick={() => { setConfirmEnd(false); setUnload(null); }} data-testid="tour-end-cancel">
            Zurück zur Tour
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Meine Tour heute" subtitle="Außendienst / Techniker">
      <div className="mx-auto max-w-md">
        <div className="mb-4 grid grid-cols-2 gap-2" data-testid="tech-tabs">
          <button onClick={() => switchTab("tour")} data-testid="tab-tour"
            className={`flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${tab === "tour" ? "bg-[#1b4332] text-white shadow-sm" : "border border-slate-200 bg-white text-slate-600"}`}>
            <ClipboardCheck className="h-4 w-4" /> Tour
          </button>
          <button onClick={() => switchTab("kalender")} data-testid="tab-kalender"
            className={`flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${tab === "kalender" ? "bg-[#1b4332] text-white shadow-sm" : "border border-slate-200 bg-white text-slate-600"}`}>
            <CalendarDays className="h-4 w-4" /> Übersicht
          </button>
        </div>

        {tab === "kalender" && (
          <div className="space-y-3" data-testid="tech-calendar">
            {!calendar && <p className="py-8 text-center text-sm text-slate-400">Lade Übersicht…</p>}
            {(calendar || []).map((day) => (
              <div key={day.date} className={`rounded-2xl p-4 shadow-sm ${day.jobs.length ? "bg-white" : "bg-white/60"}`} data-testid={`calendar-day-${day.date}`}>
                <p className={`text-xs font-bold uppercase tracking-widest ${day.date === new Date().toISOString().slice(0, 10) ? "text-[#028090]" : "text-slate-500"}`}>
                  {deDate(day.date)}{day.date === new Date().toISOString().slice(0, 10) ? " · Heute" : ""}
                </p>
                {day.jobs.length === 0 && <p className="mt-1 text-xs text-slate-400">Keine Einsätze</p>}
                {day.jobs.map((j) => (
                  <div key={j.id} className="mt-2 rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={`calendar-job-${j.order_number}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono2 text-xs font-bold text-[#0f172a]">{j.order_number}</span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600">{STATUS_LABELS[j.status] || j.status}</span>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-slate-800">{j.customer?.name} · {j.customer?.zip} {j.customer?.city}</p>
                    <p className="text-xs text-slate-500">{j.appliance?.brand} {j.appliance?.type_label} · {j.time_window || "Zeit offen"}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {tab === "tour" && (
        <div className="space-y-3" data-testid="tech-jobs-list">
        {tourEnded && (
          <div className="rounded-2xl border border-[#1b4332]/30 bg-[#e8f0ec] p-3 text-center text-sm font-semibold text-[#1b4332]" data-testid="tour-ended-banner">
            Tour beendet – Rücklagerung abgeschlossen. Feierabend!
            <button onClick={openSummary} className="mt-1.5 block w-full text-xs font-bold text-[#028090] underline" data-testid="summary-open">
              Tagesabschluss anzeigen
            </button>
          </div>
        )}
        {jobs.map((j, i) => (
          <button key={j.id} onClick={() => openJob(j)} data-testid={`job-card-${j.order_number}`}
            className="w-full rounded-2xl bg-slate-900 p-5 text-left text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 active:scale-95">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <span className="rounded-full bg-[#2ec4b6] px-2.5 py-0.5 text-xs font-bold text-[#1b4332]">Stopp {i + 1}</span>
                <span className="font-mono2 text-xs font-bold text-white/90">{j.order_number}</span>
              </span>
              <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs">{STATUS_LABELS[j.status]}</span>
            </div>
            <p className="mt-3 text-lg font-bold">{j.customer?.name}</p>
            <p className="text-sm text-slate-300">{j.customer?.street}, {j.customer?.zip} {j.customer?.city}</p>
            <p className="mt-1 text-sm text-slate-400">{j.appliance?.brand} {j.appliance?.type_label} · {j.time_window || "Zeit offen"}</p>
          </button>
        ))}
        {jobs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-slate-400" data-testid="tech-no-jobs">
            Keine Einsätze zugewiesen. Gute Pause!
          </div>
        )}
        {!tourEnded && (
          <button onClick={openUnload} disabled={busy} data-testid="tour-end-button"
            className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#1b4332] bg-white py-3.5 text-sm font-bold text-[#1b4332] shadow-sm transition-all active:scale-95">
            <PackageOpen className="h-4 w-4" /> Tour Ende – Teile zurück einlagern
          </button>
        )}
        <button onClick={toggleDone} className="w-full rounded-xl border border-slate-300 bg-white py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50" data-testid="toggle-done-jobs">
          {showDone ? "▾" : "▸"} Abgeschlossene Aufträge {showDone ? "ausblenden" : "einblenden"}{doneJobs !== null && ` (${doneJobs.length})`}
        </button>
        {showDone && (doneJobs || []).map((j) => (
          <button key={j.id} onClick={() => openJob(j)} data-testid={`done-job-${j.order_number}`}
            className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left opacity-75 transition-opacity hover:opacity-100">
            <div className="flex items-center justify-between">
              <span className="font-mono2 text-sm font-bold text-[#0f172a]">{j.order_number}</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{STATUS_LABELS[j.status]}</span>
            </div>
            <p className="mt-1 text-sm text-slate-600">{j.customer?.name} · {j.appliance?.type_label}</p>
            {j.payment && <p className="text-xs text-slate-400">{eur(j.payment.amount)} · {j.payment.paid ? "Bezahlt" : "Offen"}</p>}
          </button>
        ))}
        {showDone && doneJobs !== null && doneJobs.length === 0 && (
          <p className="text-center text-sm text-slate-400" data-testid="done-jobs-empty">Noch keine abgeschlossenen Aufträge.</p>
        )}
        </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Play2() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
